import React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { IMobileAccess } from "@nju/data-access-portal-product";
import { ErrorMessage, Input } from "@nju/form";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { BlockText, Button, Panel, Text } from "@nju/ui";
import { formatDate } from "@nju/utils";
import { promocodesMachine } from "./promocodes.machine";

interface IPromocodesProps {
  mobileAccess: IMobileAccess | undefined;
}

export function Promocodes({ mobileAccess }: IPromocodesProps) {
  const router = useRouter();
  const { userId } = portalRoutes.mojeKonto.uslugiDodatkowe.index
    .input(router.query)
    .unwrap();
  const [current, send] = useMachine(promocodesMachine, {
    context: {
      productId: mobileAccess?.id,
      userId,
    },
  });
  const { context, matches } = current;
  const { inputError, promoCodes, codeId, input, formError } = context;

  const onInputChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      ({ target }) => {
        const { value } = target;

        send({
          type: "SET_INPUT_VALUE",
          value,
        });
      },
      [send],
    );

  const handleSubmit: React.FormEventHandler<HTMLFormElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        send({ type: "SUBMIT" });
      },
      [send],
    );

  const handleActivate = React.useCallback(
    ({
      codeId,
      targetUserId,
      subId,
    }: {
      codeId: string;
      targetUserId: string | undefined;
      subId: string | undefined;
    }) => {
      send({ type: "ACTIVATE_PROMO_CODE", codeId, targetUserId, subId });
    },
    [send],
  );

  const readyCodes =
    promoCodes?.digital?.filter((item) => {
      return item.state === "READY";
    }) || [];

  return (
    <Panel
      customPadding={true}
      iconName="gift"
      invisibleSeparator={true}
      isFoldable={true}
      separatorSize="none"
      title="Twój kod promocyjny"
    >
      {({ isExpanded }) => {
        return (
          <>
            {isExpanded && (
              <>
                <div className="p-6 bg-[#EAF0F0] rounded-[12px] mt-4">
                  <Text
                    size={{ "@onlyMobile": "l", "@fromTablet": "xl" }}
                    weight="bold"
                  >
                    masz kod rabatowy?
                  </Text>
                  <form className="mt-6" onSubmit={handleSubmit}>
                    <div className="flex items-center gap-2 flex-col from-tablet:flex-row">
                      <div className="flex-1 w-full">
                        <Input
                          isDisabled={
                            matches("addPromoCode") ||
                            matches("fetchPromoCodes")
                          }
                          label="wprowadź kod promocyjny"
                          name="input"
                          onChange={onInputChange}
                          state={inputError ? "error" : undefined}
                          value={input}
                        />
                      </div>
                      <Button
                        display={{ "@onlyMobile": "block" }}
                        isLoading={
                          matches("addPromoCode") || matches("fetchPromoCodes")
                        }
                        size={{ "@onlyMobile": "s" }}
                        type="submit"
                        variant="secondary"
                      >
                        potwierdź kod
                      </Button>
                    </div>
                    {inputError && <ErrorMessage message={inputError} />}
                  </form>
                </div>
                {readyCodes.length > 0 && (
                  <>
                    {readyCodes.map((item, index) => {
                      return (
                        <div className="mt-4" key={index}>
                          <div className="rounded-[12px] border-brightCerulean border-dashed border-1 p-4 flex justify-between items-center flex-col from-tablet:flex-row gap-4">
                            <div className="flex gap-2">
                              <Icon
                                height={20}
                                name="percentage"
                                tone="accent"
                                width={20}
                              />
                              <div>
                                <BlockText size="m" weight="bold">
                                  {item.offer.description}
                                </BlockText>
                                <BlockText size="l">
                                  aktywuj do{" "}
                                  {formatDate(
                                    item.offer.validUntilDate,
                                    true,
                                    ".",
                                    " ",
                                  )}
                                </BlockText>
                              </div>
                            </div>
                            <Button
                              display={{ "@onlyMobile": "block" }}
                              isDisabled={
                                matches("activatePromoCode") &&
                                codeId !== item.id
                              }
                              isLoading={
                                matches("activatePromoCode") &&
                                codeId === item.id
                              }
                              onPress={() => {
                                return handleActivate({
                                  codeId: item.id,
                                  targetUserId: userId,
                                  subId: mobileAccess?.id,
                                });
                              }}
                              size={{ "@onlyMobile": "s" }}
                            >
                              aktywuj
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
                {formError && (
                  <div className="mt-4">
                    <ErrorMessage message={formError} />
                  </div>
                )}
              </>
            )}
          </>
        );
      }}
    </Panel>
  );
}
